import { FormHTMLAttributes, ReactNode } from "react";

import Card from "../card";
import Typography from "../typography";
import { cn } from "../../utls/utils";

export type ThemedFormSpacing = "sm" | "md" | "lg";

export interface ThemedFormContainerProps
  extends Omit<FormHTMLAttributes<HTMLFormElement>, "title"> {
  /**
   * Main heading displayed in the form header. Strings are styled automatically,
   * while custom nodes render as-is.
   */
  title?: ReactNode;
  /**
   * Supporting copy that appears under the title in the header area.
   */
  description?: ReactNode;
  /**
   * Slot rendered to the right of the title—use for contextual actions or links.
   */
  actions?: ReactNode;
  /**
   * Optional error banner rendered above the children.
   */
  errorMessage?: ReactNode;
  /**
   * Optional success banner rendered above the children.
   */
  successMessage?: ReactNode;
  /**
   * Content rendered in the footer area, typically primary/secondary actions.
   */
  footer?: ReactNode;
  /**
   * Controls the vertical spacing rhythm within the body.
   */
  spacing?: ThemedFormSpacing;
  /**
   * Additional class names applied to the root Card.
   */
  cardClassName?: string;
  /**
   * Additional class names applied to the wrapping form element.
   */
  formClassName?: string;
  /**
   * Additional class names applied to the header section.
   */
  headerClassName?: string;
  /**
   * Additional class names applied to the body section.
   */
  bodyClassName?: string;
  /**
   * Additional class names applied to the footer section.
   */
  footerClassName?: string;
  children: ReactNode;
}

const spacingClasses: Record<ThemedFormSpacing, string> = {
  sm: "space-y-4",
  md: "space-y-6",
  lg: "space-y-8",
};

export function ThemedFormContainer({
  title,
  description,
  actions,
  errorMessage,
  successMessage,
  footer,
  spacing = "md",
  cardClassName,
  formClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  className,
  children,
  ...formProps
}: ThemedFormContainerProps) {
  return (
    <form
      {...formProps}
      className={cn("w-full", className, formClassName)}
    >
      <Card
        variant="cream"
        elevation={2}
        className={cn(
          "flex h-full w-full flex-col overflow-hidden border border-cream-200 bg-cream-50",
          cardClassName
        )}
      >
        {(title || description || actions) && (
          <Card.Header
            variant="cream"
            divider
            className={cn(
              "flex flex-col gap-4 bg-cream-100 sm:flex-row sm:items-start sm:justify-between",
              headerClassName
            )}
          >
            <div className="space-y-1">
              {typeof title === "string" ? (
                <Typography variant="h4" className="text-terracotta-700">
                  {title}
                </Typography>
              ) : (
                title ?? null
              )}
              {typeof description === "string" ? (
                <Typography variant="body2" className="text-brown-600">
                  {description}
                </Typography>
              ) : (
                description ?? null
              )}
            </div>
            {actions ? <div className="shrink-0">{actions}</div> : null}
          </Card.Header>
        )}

        <Card.Body
          className={cn(
            "flex-1 bg-white",
            spacingClasses[spacing],
            bodyClassName
          )}
        >
          {errorMessage ? (
            <div
              role="alert"
              className="rounded-md border border-terracotta-200 bg-terracotta-50 p-4"
            >
              {typeof errorMessage === "string" ? (
                <Typography variant="body2" className="text-terracotta-700">
                  {errorMessage}
                </Typography>
              ) : (
                errorMessage
              )}
            </div>
          ) : null}

          {successMessage ? (
            <div
              role="status"
              className="rounded-md border border-emerald-200 bg-emerald-50 p-4"
            >
              {typeof successMessage === "string" ? (
                <Typography variant="body2" className="text-emerald-700">
                  {successMessage}
                </Typography>
              ) : (
                successMessage
              )}
            </div>
          ) : null}

          {children}
        </Card.Body>

        {footer ? (
          <Card.Footer
            variant="cream"
            className={cn(
              "flex flex-col gap-3 bg-cream-100 sm:flex-row sm:items-center sm:justify-end",
              footerClassName
            )}
          >
            {footer}
          </Card.Footer>
        ) : null}
      </Card>
    </form>
  );
}

export default ThemedFormContainer;
